import {
  type DatumAbstraction,
  type FoundationLateralDatumAbstraction,
  type FoundationVerticalDatumAbstraction,
} from "building-types/datums/datumAbstractionTypes";
import type { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { DatumLateralUsageType, DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import { DrawingId } from "building-types/drawings/drawingTypesEnum";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationAssemblyEntityDatums } from "building-types/foundations/foundationAssemblyEntityDatumsTypes";
import type {
  FoundationAssembly,
  FoundationAssemblyEntities,
  FoundationAssemblyEntity,
  FoundationAssemblyOverrides,
  FoundationConditionData,
  FoundationSystemAssemblyOverride,
  SolidFoundationAssembly,
  VoidFoundationAssembly,
  FoundationAssemblyComponent,
  IsolatedFoundationPickSource,
} from "building-types/foundations/foundationAssemblyEntityTypes";
import { isSolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import type {
  CurbEntity,
  FootingEntity,
  PierEntity,
  StemWallEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import { isValidFoundationAssemblyComponent } from "building-types/foundations/foundationComponents";
import type {
  FoundationSubType,
  FoundationSuperSystemType,
  FoundationSuperType,
} from "building-types/foundations/foundationEnums";
import {
  BrickLedgeBehavior,
  BrickLedgeType,
  FootingType,
  FoundationCondition,
  FoundationSubSystemType,
  SlabType,
} from "building-types/foundations/foundationEnums";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPattern,
  GeneratedIsolatedFoundationPattern,
  PierPosition,
} from "building-types/foundations/foundationPatternTypes";
import type { FoundationPath } from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { Level } from "building-types/levels/levelTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { foundationLateralDatumEntityNew } from "doc/datums/datumEntityNew";
import { CURB_GARAGE_COMPONENT_ID } from "doc/elements/foundation/foundationComponentsTools";
import { defaultFoundationAssemblyId } from "doc/elements/foundations/defaultFoundationAssemblies";
import { defaultFoundationComponentId } from "doc/elements/foundations/defaultFoundationComponents";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { foundationHash } from "doc/utils/foundationSystemTools";
import {
  isDisabledFoundationCondition,
  isGarageFoundationCondition,
  isExteriorFoundationCondition,
  isSpanFoundationCondition,
} from "doc/utils/foundationSystemTools";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { createDatumAbstraction } from "../datums/datumAbstractionFactory";
import { floorPathBottom, floorPathTop } from "../floor/floorPathUtils";
import {
  collectIsolatedFoundationHalfEdgePatterns,
  foundationHalfEdgePatterns,
  isVoidFoundationPattern,
} from "./foundationPattern";
import { foundationPathCoordinates } from "./pathCoordinates";
import { slabTypeAtRoomClass } from "./slabUtils";
import { stemWallBaseWidth, stemWallTop } from "./stemWall";

function foundationAssemblyComponentNew<T extends FoundationComponentType>(
  components: Table<FoundationComponentEntity>,
  componentSystem: FoundationSubSystemType,
  componentType: T,
  componentId: string,
): FoundationAssemblyComponent<T> {
  const defaultId = defaultFoundationComponentId(componentSystem, componentType);
  const component = components[componentId] ?? (defaultId !== undefined ? components[defaultId] : undefined);

  let finalComponentId: string | undefined = componentId;

  if (
    component.componentType !== componentType ||
    !isValidFoundationAssemblyComponent(components, componentSystem, componentId)
  ) {
    // This can happen when a component is deleted from the catalog and references are still maintained in the
    // assembly (either because of docRef changes or user error)
    // TODO - surface these kinds of issues before they hit this code point?
    finalComponentId = defaultFoundationComponentId(componentSystem, componentType);
  }

  assertIsNotUndefined(finalComponentId);

  return {
    ...docEntityBaseNew("FOUNDATION_COMPONENT"),
    componentType,
    componentId: finalComponentId,
  };
}

export function foundationAssemblyFootingComponentNew(
  components: Table<FoundationComponentEntity>,
  componentSystem: FoundationSubSystemType,
  componentId: string,
) {
  return foundationAssemblyComponentNew(components, componentSystem, FoundationComponentType.FOOTING, componentId);
}

export function foundationAssemblyStemWallComponentNew(
  components: Table<FoundationComponentEntity>,
  componentSystem: FoundationSubSystemType,
  componentId: string,
) {
  return foundationAssemblyComponentNew(components, componentSystem, FoundationComponentType.STEMWALL, componentId);
}

export function foundationAssemblyCurbComponentNew(
  components: Table<FoundationComponentEntity>,
  componentSystem: FoundationSubSystemType,
  componentId: string,
) {
  return foundationAssemblyComponentNew(components, componentSystem, FoundationComponentType.CURB, componentId);
}

export function foundationAssemblyPierComponentNew(
  components: Table<FoundationComponentEntity>,
  componentSystem: FoundationSubSystemType,
  componentId: string,
) {
  return foundationAssemblyComponentNew(components, componentSystem, FoundationComponentType.PIER, componentId);
}

export function isVoidFoundationAssembly(assembly: FoundationAssembly): assembly is VoidFoundationAssembly {
  return assembly.assemblyType === FoundationAssemblyType.VOID;
}

export function voidFoundationAssemblyNew({
  lateralDatumAbstraction,
}: {
  lateralDatumAbstraction: FoundationLateralDatumAbstraction;
}): VoidFoundationAssembly {
  return {
    assemblyType: FoundationAssemblyType.VOID,
    lateralDatumAbstraction,
  };
}

function solidFoundationAssemblyNew({
  assemblyId,
  lateralDatumAbstraction,
  topDatumAbstraction,
  bottomDatumAbstraction,
  brickLedgeTopDatumAbstraction,
  stemWall,
  footing,
  pier,
  curb,
  drawingId,
  drawingNumber,
  pierPositions,
  pierOffset,
  pierRotation,
  pickSource,
}: {
  assemblyId: string;
  lateralDatumAbstraction: FoundationLateralDatumAbstraction;
  topDatumAbstraction?: FoundationVerticalDatumAbstraction;
  bottomDatumAbstraction?: FoundationVerticalDatumAbstraction;
  brickLedgeTopDatumAbstraction?: FoundationVerticalDatumAbstraction;
  stemWall?: FoundationAssemblyComponent<FoundationComponentType.STEMWALL>;
  footing?: FoundationAssemblyComponent<FoundationComponentType.FOOTING>;
  pier?: FoundationAssemblyComponent<FoundationComponentType.PIER>;
  curb?: FoundationAssemblyComponent<FoundationComponentType.CURB>;
  drawingId?: DrawingId | ForeignEntityId<"DRAWING">;
  drawingNumber?: number;
  pierPositions?: PierPosition[];
  pierOffset?: number[];
  pierRotation?: number;
  pickSource?: IsolatedFoundationPickSource;
}): SolidFoundationAssembly {
  return {
    assemblyType: FoundationAssemblyType.SOLID,
    lateralDatumAbstraction,
    topDatumAbstraction,
    bottomDatumAbstraction,
    brickLedgeTopDatumAbstraction,
    assemblyId,
    stemWall,
    footing,
    curb,
    pier,
    drawingId,
    drawingNumber,
    pierPositions,
    pierOffset,
    pierRotation,
    pickSource,
  };
}

export function makeSolidFoundationAssembly({
  assemblyId,
  foundationAssemblyComponents,
  foundationComponents,
  foundationSettings,
  datumAbstractions,
  foundationAssemblyOverride,
  pierPositions = [],
  pierOffsetX = 0,
  pierOffsetY = 0,
  pierRotation = 0,
  topDatumIdInstanceOverride,
  bottomDatumIdInstanceOverride,
  lateralDatumIdInstanceOverride,
  brickledgeTopDatumIdInstanceOverride,
  pickSource,
}: {
  assemblyId: string;
  foundationAssemblyComponents: FoundationAssemblyEntities;
  foundationComponents: Table<FoundationComponentEntity>;
  foundationSettings: FoundationSystemSettings;
  datumAbstractions: Table<DatumAbstraction>;
  foundationAssemblyOverride?: FoundationAssemblyEntityDatums & {
    drawingId?: DrawingId | ForeignEntityId<"DRAWING">;
    drawingNumber?: number;
  };
  pierPositions?: PierPosition[];
  pierOffsetX?: number;
  pierOffsetY?: number;
  pierRotation?: number;
  topDatumIdInstanceOverride?: string;
  bottomDatumIdInstanceOverride?: string;
  lateralDatumIdInstanceOverride?: string;
  brickledgeTopDatumIdInstanceOverride?: string;
  pickSource?: IsolatedFoundationPickSource;
}): SolidFoundationAssembly {
  const { assemblySystem, footingId, stemWallId, curbId, pierId } = foundationAssemblyComponents;

  const footing = footingId
    ? foundationAssemblyFootingComponentNew(foundationComponents, assemblySystem, footingId)
    : undefined;

  const stemWall = stemWallId
    ? foundationAssemblyStemWallComponentNew(foundationComponents, assemblySystem, stemWallId)
    : undefined;

  const curb = curbId ? foundationAssemblyCurbComponentNew(foundationComponents, assemblySystem, curbId) : undefined;

  const pier = pierId ? foundationAssemblyPierComponentNew(foundationComponents, assemblySystem, pierId) : undefined;

  // initialize with additional instance overrides (will be overridden by foundationAssemblyComponents and foundationAssemblyOverride)
  let topDatumId: string | undefined;
  let bottomDatumId: string | undefined;
  let lateralDatumId: string | undefined;
  let brickledgeTopDatumId: string | undefined;

  if (topDatumIdInstanceOverride && datumAbstractions[topDatumIdInstanceOverride]) {
    topDatumId = topDatumIdInstanceOverride;
  }

  if (bottomDatumIdInstanceOverride && datumAbstractions[bottomDatumIdInstanceOverride]) {
    bottomDatumId = bottomDatumIdInstanceOverride;
  }

  if (lateralDatumIdInstanceOverride && datumAbstractions[lateralDatumIdInstanceOverride]) {
    lateralDatumId = lateralDatumIdInstanceOverride;
  }

  if (brickledgeTopDatumIdInstanceOverride && datumAbstractions[brickledgeTopDatumIdInstanceOverride]) {
    brickledgeTopDatumId = brickledgeTopDatumIdInstanceOverride;
  }

  // apply overrides

  let drawingId: DrawingId | ForeignEntityId<"DRAWING"> | undefined;
  let drawingNumber: number | undefined;
  const hasDrawingOverride =
    foundationAssemblyOverride?.drawingId !== undefined || foundationAssemblyOverride?.drawingNumber !== undefined;
  if (hasDrawingOverride) {
    drawingId = foundationAssemblyOverride?.drawingId ?? DrawingId.NONE;
    drawingNumber = foundationAssemblyOverride?.drawingNumber ?? -1;
  }

  /**
   * Updates datum IDs from source if they exist and are valid in datumAbstractions.
   */
  const updateDatumIdsFromSource = (
    source: {
      lateralDatumId?: string;
      topDatumId?: string;
      bottomDatumId?: string;
      brickledgeTopDatumId?: string;
    },
    current: {
      lateralDatumId: string | undefined;
      topDatumId: string | undefined;
      bottomDatumId: string | undefined;
      brickledgeTopDatumId: string | undefined;
    },
  ): {
    lateralDatumId: string | undefined;
    topDatumId: string | undefined;
    bottomDatumId: string | undefined;
    brickledgeTopDatumId: string | undefined;
  } => {
    const updated = { ...current };
    if (source.lateralDatumId && datumAbstractions[source.lateralDatumId]) {
      updated.lateralDatumId = source.lateralDatumId;
    }
    if (source.topDatumId && datumAbstractions[source.topDatumId]) {
      updated.topDatumId = source.topDatumId;
    }
    if (source.bottomDatumId && datumAbstractions[source.bottomDatumId]) {
      updated.bottomDatumId = source.bottomDatumId;
    }
    if (source.brickledgeTopDatumId && datumAbstractions[source.brickledgeTopDatumId]) {
      updated.brickledgeTopDatumId = source.brickledgeTopDatumId;
    }
    return updated;
  };

  // override with foundationAssemblyComponents values
  ({ lateralDatumId, topDatumId, bottomDatumId, brickledgeTopDatumId } = updateDatumIdsFromSource(
    foundationAssemblyComponents,
    { lateralDatumId, topDatumId, bottomDatumId, brickledgeTopDatumId },
  ));

  // override with foundationAssemblyOverride values
  if (foundationAssemblyOverride) {
    ({ lateralDatumId, topDatumId, bottomDatumId, brickledgeTopDatumId } = updateDatumIdsFromSource(
      foundationAssemblyOverride,
      { lateralDatumId, topDatumId, bottomDatumId, brickledgeTopDatumId },
    ));
  }

  // create datums
  let lateralDatum: WallLateralDatumAnchor = foundationSettings.foundationPerimeterDatum;
  let lateralDatumOffset: number = foundationSettings.foundationPerimeterDatumOffset;
  let lateralDatumIdToPreserve: string | undefined;
  if (lateralDatumId && datumAbstractions[lateralDatumId]) {
    const datumAbstraction = datumAbstractions[lateralDatumId];
    if (datumAbstraction?.isValidForUsage(DatumLateralUsageType.FOUNDATION_LATERAL)) {
      const { anchor, offset } = datumAbstraction.getDatumEntity();
      lateralDatum = anchor ?? lateralDatum;
      lateralDatumOffset = offset ?? lateralDatumOffset;
      lateralDatumIdToPreserve = lateralDatumId;
    }
  }

  const lateralDatumAbstraction = createDatumAbstraction(
    foundationLateralDatumEntityNew({
      id: lateralDatumIdToPreserve,
      anchor: lateralDatum,
      offset: lateralDatumOffset,
    }),
  ) as FoundationLateralDatumAbstraction;

  let topDatumAbstraction: FoundationVerticalDatumAbstraction | undefined;
  if (topDatumId && datumAbstractions[topDatumId]) {
    const datumAbstraction = datumAbstractions[topDatumId];
    if (datumAbstraction?.isValidForUsage(DatumVerticalUsageType.FOUNDATION_VERTICAL)) {
      topDatumAbstraction = datumAbstraction;
    }
  }

  let bottomDatumAbstraction: FoundationVerticalDatumAbstraction | undefined;
  if (bottomDatumId && datumAbstractions[bottomDatumId]) {
    const datumAbstraction = datumAbstractions[bottomDatumId];
    if (datumAbstraction?.isValidForUsage(DatumVerticalUsageType.FOUNDATION_VERTICAL)) {
      bottomDatumAbstraction = datumAbstraction;
    }
  }

  let brickLedgeTopDatumAbstraction: FoundationVerticalDatumAbstraction | undefined;
  if (brickledgeTopDatumId && datumAbstractions[brickledgeTopDatumId]) {
    const datumAbstraction = datumAbstractions[brickledgeTopDatumId];
    if (datumAbstraction?.isValidForUsage(DatumVerticalUsageType.FOUNDATION_VERTICAL)) {
      brickLedgeTopDatumAbstraction = datumAbstraction;
    }
  }

  return solidFoundationAssemblyNew({
    assemblyId,
    lateralDatumAbstraction,
    topDatumAbstraction,
    bottomDatumAbstraction,
    brickLedgeTopDatumAbstraction,
    stemWall,
    footing,
    pier,
    curb,
    drawingId,
    drawingNumber,
    pierPositions,
    pierOffset: [pierOffsetX, pierOffsetY],
    pierRotation,
    pickSource,
  });
}

function makeFoundationSubSystemAssembly({
  foundationSettings,
  foundationAssemblies,
  foundationComponents,
  datumAbstractions,
}: {
  foundationSettings: FoundationSystemSettings;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationComponents: Table<FoundationComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
}): FoundationAssembly {
  const { foundationSubSystemType, foundationAssembly: foundationAssemblyId } = foundationSettings;

  let assemblyId = foundationAssemblyId;
  let assemblyComponents = foundationAssemblies[foundationAssemblyId];
  if (!assemblyComponents || !assemblyId) {
    assemblyId = defaultFoundationAssemblyId(foundationSubSystemType);
    assemblyComponents = foundationAssemblies[assemblyId];
  }

  return makeSolidFoundationAssembly({
    assemblyId,
    foundationAssemblyComponents: assemblyComponents,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
  });
}

export function makeRoomEdgeIsolatedFoundationAssemblies({
  foundationPath,
  foundationSettings,
  foundationAssemblies,
  foundationComponents,
  datumAbstractions,
  foundationPatterns,
  foundationPathsBelow,
  planarGraphs,
}: {
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "condition" | "planarGraphId">;
  foundationSettings: FoundationSystemSettings;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationComponents: Table<FoundationComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
  foundationPatterns?: HalfEdgeAssociationTable<FoundationPattern>;
  foundationPathsBelow?: Pick<FoundationPath, "halfEdgeIndex" | "condition">[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationAssembly[] {
  const { halfEdgeIndex, condition: foundationCondition } = foundationPath;
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
  const isolatedFoundationAssemblies: FoundationAssembly[] = [];

  const isolatedPatternOverrides = collectIsolatedFoundationHalfEdgePatterns(
    halfEdgeIndex,
    foundationPatterns,
    startX,
    startY,
    endX,
    endY,
  );

  if (
    isNotValidFoundationAssembly({
      foundationCondition,
      foundationPatterns: isolatedPatternOverrides,
      sectionSizeType: FoundationSectionSizeType.ISOLATED,
      foundationPathsBelow,
    })
  ) {
    return isolatedFoundationAssemblies;
  }

  isolatedPatternOverrides.forEach((pattern) => {
    const {
      positions: pos,
      assemblyId: id,
      xOffset,
      yOffset,
      pierRotation,
      topDatumId,
      bottomDatumId,
      edgeBehaviorId,
    } = pattern;
    const isolatedFoundationAssembly = id ? foundationAssemblies[id] : undefined;
    if (isolatedFoundationAssembly) {
      isolatedFoundationAssemblies.push(
        makeSolidFoundationAssembly({
          assemblyId: id,
          foundationAssemblyComponents: isolatedFoundationAssembly,
          foundationComponents,
          foundationSettings,
          datumAbstractions,
          pierPositions: pos,
          pierOffsetX: xOffset,
          pierOffsetY: yOffset,
          pierRotation,
          topDatumIdInstanceOverride: topDatumId,
          bottomDatumIdInstanceOverride: bottomDatumId,
          pickSource: edgeBehaviorId
            ? {
                kind: "edgeBehavior",
                edgeBehaviorId,
              }
            : undefined,
        }),
      );
    }
  });

  return isolatedFoundationAssemblies;
}

export function makeGeneratedIsolatedFoundationAssemblies({
  foundationPath,
  foundationSettings,
  foundationAssemblies,
  foundationComponents,
  datumAbstractions,
  generatedIsolatedFoundationPatterns,
  planarGraphs,
}: {
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "planarGraphId">;
  foundationSettings: FoundationSystemSettings;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationComponents: Table<FoundationComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
  generatedIsolatedFoundationPatterns?: HalfEdgeAssociationTable<GeneratedIsolatedFoundationPattern>;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationAssembly[] {
  const { halfEdgeIndex } = foundationPath;
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
  const isolatedFoundationAssemblies: FoundationAssembly[] = [];

  if (generatedIsolatedFoundationPatterns) {
    const isolatedPatternOverrides = collectIsolatedFoundationHalfEdgePatterns(
      halfEdgeIndex,
      generatedIsolatedFoundationPatterns,
      startX,
      startY,
      endX,
      endY,
    );
    isolatedPatternOverrides.forEach((pattern) => {
      const {
        positions: pos,
        assemblyId: id,
        xOffset,
        yOffset,
        pierRotation,
        topDatumId,
        bottomDatumId,
        sourcePickHandle,
      } = pattern;
      const isolatedFoundationAssembly = id ? foundationAssemblies[id] : undefined;
      if (isolatedFoundationAssembly) {
        isolatedFoundationAssemblies.push(
          makeSolidFoundationAssembly({
            assemblyId: id,
            foundationAssemblyComponents: isolatedFoundationAssembly,
            foundationComponents,
            foundationSettings,
            datumAbstractions,
            pierPositions: pos,
            pierOffsetX: xOffset,
            pierOffsetY: yOffset,
            pierRotation,
            topDatumIdInstanceOverride: topDatumId,
            bottomDatumIdInstanceOverride: bottomDatumId,
            pickSource: sourcePickHandle
              ? {
                  kind: "sourceEntity",
                  sourcePickHandle,
                }
              : undefined,
          }),
        );
      }
    });
  }

  return isolatedFoundationAssemblies;
}

export function makeFoundationAssembly({
  level,
  foundationPath,
  floorPath,
  foundationSettings,
  foundationAssemblies,
  foundationComponents,
  datumAbstractions,
  foundationPatterns,
  foundationAssemblyOverride,
  foundationPathsBelow,
}: {
  level: Level;
  foundationPath: Pick<
    /**
     * @NOTE - everything required for making a foundation assembly exists on the WallPath,
     * other than the FoundationCondition. This is another good indicator that foundation/floor paths should be integrated
     * into wallPaths
     */
    FoundationPath,
    "halfEdgeIndex" | "condition" | "datums"
  >;
  floorPath: Pick<
    FloorPath,
    "floorPriority" | "leftWallRoom" | "rightWallRoom" | "leftFloorAssembly" | "rightFloorAssembly"
  >;
  foundationSettings: FoundationSystemSettings;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationComponents: Table<FoundationComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
  foundationPatterns?: HalfEdgeAssociationTable<FoundationPattern>;
  foundationAssemblyOverride?: FoundationSystemAssemblyOverride<
    (typeof foundationSettings)["foundationSuperSystemType"],
    (typeof foundationSettings)["foundationSubSystemType"]
  >;
  foundationPathsBelow?: Pick<FoundationPath, "halfEdgeIndex" | "condition">[];
  floorPathsBelow?: FloorPath[];
}): FoundationAssembly {
  const { halfEdgeIndex, condition: foundationCondition } = foundationPath;
  const { foundationSubSystemType, foundationConditions, foundationPerimeterDatum, foundationPerimeterDatumOffset } =
    foundationSettings;

  if (
    isNotValidFoundationAssembly({
      foundationCondition,
      foundationPatterns: foundationHalfEdgePatterns(halfEdgeIndex, foundationPatterns),
      sectionSizeType: FoundationSectionSizeType.LINEAR,
      foundationPathsBelow,
    })
  ) {
    return voidFoundationAssemblyNew({
      lateralDatumAbstraction: createDatumAbstraction(
        foundationLateralDatumEntityNew({
          anchor: foundationPerimeterDatum,
          offset: foundationPerimeterDatumOffset,
        }),
      ) as FoundationLateralDatumAbstraction,
    });
  }

  const conditionData = foundationConditions[foundationCondition];
  const foundationOverrideAssemblyId = foundationAssemblyOverride?.assemblyId;
  const foundationConditionAssemblyId = conditionData?.assemblyId;
  const foundationSettingsAssemblyId = foundationSettings.foundationAssembly;
  const foundationDefaultAssemblyId = defaultFoundationAssemblyId(foundationSubSystemType);

  // fallback assembly waterfall => override => condition => settings => default
  let conditionAssemblyId =
    foundationOverrideAssemblyId ??
    foundationConditionAssemblyId ??
    foundationSettingsAssemblyId ??
    foundationDefaultAssemblyId;

  let conditionAssemblyComponents = foundationAssemblies[conditionAssemblyId];

  if (!conditionAssemblyComponents) {
    conditionAssemblyId = foundationDefaultAssemblyId;
    conditionAssemblyComponents = foundationAssemblies[foundationDefaultAssemblyId];
  }

  // auto generated component
  if (
    foundationAssemblyShouldAddCurb({
      level,
      datumAbstractions,
      foundationPath,
      floorPath,
      foundationAssemblyComponents: conditionAssemblyComponents,
      foundationSettings,
      foundationComponents,
      foundationAssemblyOverride,
    })
  ) {
    let curbId: string | undefined;
    if (isGarageFoundationCondition[foundationPath.condition]) {
      curbId = CURB_GARAGE_COMPONENT_ID;
    } else {
      curbId = defaultFoundationComponentId(foundationSettings.foundationSubSystemType, FoundationComponentType.CURB);
    }
    conditionAssemblyComponents = { ...conditionAssemblyComponents, curbId };
  }

  return makeSolidFoundationAssembly({
    assemblyId: conditionAssemblyId,
    foundationAssemblyComponents: conditionAssemblyComponents,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
    foundationAssemblyOverride,
  });
}

/**
 * This function is for internal foundation assembly calculation only. It is used in the generation of final
 * foundation assemblies to determine whether to generate a Curb component at a given half edge.
 */
function foundationAssemblyShouldAddCurb({
  level,
  foundationPath,
  floorPath,
  foundationSettings,
  foundationAssemblyComponents,
  foundationComponents,
  datumAbstractions,
  foundationAssemblyOverride,
}: {
  level: Level;
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "condition" | "datums">;
  floorPath: Pick<
    FloorPath,
    "leftWallRoom" | "rightWallRoom" | "leftFloorAssembly" | "rightFloorAssembly" | "floorPriority"
  >;
  foundationSettings: FoundationSystemSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
  foundationAssemblyComponents: FoundationAssemblyEntity;
  foundationAssemblyOverride?: FoundationSystemAssemblyOverride<
    (typeof foundationSettings)["foundationSuperSystemType"],
    (typeof foundationSettings)["foundationSubSystemType"]
  >;
}) {
  const { leftWallRoom, rightWallRoom, floorPriority } = floorPath;

  const leftRoom = leftWallRoom.room;
  const rightRoom = rightWallRoom.room;

  // this is for internal use only, since it will not return an
  // accurate result for an arbitrary wall path
  function foundationPathHasCurb(): boolean {
    if (floorPriority === PathPriority.NONE) {
      // the two rooms are on the same slab/datum, so we need to pick
      const leftOpen = !!leftWallRoom.roomConnection;
      const leftHasWalls = leftRoom && isWalledRoomEnclosure[leftRoom.attributes.roomEnclosure];
      const leftHasCurb = leftHasWalls && leftRoom.attributes.hasCurb;

      const rightOpen = !!rightWallRoom.roomConnection;
      const rightHasWalls = rightRoom && isWalledRoomEnclosure[rightRoom.attributes.roomEnclosure];
      const rightHasCurb = rightHasWalls && rightRoom.attributes.hasCurb;

      if (leftOpen || rightOpen) {
        // void wall section - no curb
        return false;
      } else if (leftHasCurb && rightHasCurb) {
        // two rooms with curbs are connected with no curb
        return false;
      } else if ((leftHasCurb && !rightHasWalls) || (rightHasCurb && !leftHasWalls)) {
        // one room with a curb to an exterior room should have a curb ( typ int/ext or int/porch )
        return true;
      } else {
        // no curbs
        return false;
      }
    } else {
      // the wall will always end up on the priority room class floor
      const roomDescriptor = floorPriority === PathPriority.LEFT ? leftWallRoom : rightWallRoom;
      const room = roomDescriptor.room;
      const roomOpen = !!roomDescriptor.roomConnection;
      //what if room has void walls?
      const roomHasWalls = room && isWalledRoomEnclosure[room.attributes.roomEnclosure];
      const roomHasCurb = roomHasWalls && room.attributes.hasCurb;

      if (roomOpen) {
        return false;
      } else if (!roomHasWalls || !roomHasCurb) {
        return false;
      } else {
        return true;
      }
    }
  }

  // no curb - easy out
  if (!foundationPathHasCurb()) {
    return false;
  }

  // we create a temporary foundation assembly here to check the stem wall against the curb
  const noCurbAssembly = makeSolidFoundationAssembly({
    assemblyId: foundationAssemblyComponents.name,
    foundationAssemblyComponents,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
    foundationAssemblyOverride,
  });
  const { stemWall } = buildFoundationAssemblyComponents(foundationComponents, noCurbAssembly);
  if (stemWall) {
    const pathFloorTop = floorPathTop(level, floorPath);
    const pathStemTop = stemWallTop({
      foundationPath: { ...foundationPath, assembly: noCurbAssembly },
      stemWall,
      level,
      slabSettings: foundationSettings,
    });
    // this path has a curb, but the stem wall supercedes it so it should be dropped
    if (pathStemTop > pathFloorTop) {
      return false;
    }
  }
  return true;
}
function isNotValidFoundationAssembly({
  foundationCondition,
  sectionSizeType,
  foundationPatterns = [],
  foundationPathsBelow = [],
}: {
  foundationCondition: FoundationCondition;
  foundationPatterns: FoundationPattern[];
  sectionSizeType: FoundationSectionSizeType;
  foundationPathsBelow?: Pick<FoundationPath, "halfEdgeIndex" | "condition">[];
}) {
  // find overrides
  const hasVoidOverride = foundationPatterns?.find((fp) => isVoidFoundationPattern(fp));
  const hasIsolatedOverride = foundationPatterns?.find(({ patternType }) => patternType === sectionSizeType);

  // suppress on other conditions only if all overrides are void
  if (hasVoidOverride && !hasIsolatedOverride) {
    return true;
  }

  // suppress all on DISABLED foundation conditions
  if (isDisabledFoundationCondition[foundationCondition]) {
    return true;
  }

  // suppress all on EXTERIOR foundation conditions. This could be revisited
  if (isExteriorFoundationCondition[foundationCondition]) {
    return true;
  }

  // suppress on span conditions unlesss there is at least one isolated override
  if (isSpanFoundationCondition[foundationCondition] && !hasIsolatedOverride) {
    return true;
  }

  // // TODO - implement splits in path from level below foundation
  if (foundationPathsBelow.length) {
    return true;
  }

  return false;
}

export function foundationAssemblyTop({
  foundationPath,
  floorPath,
  foundationAssembly,
  foundationSettings,
  foundationComponents,
  framingComponents,
  slabComponents,
  sheathingComponents,
  level,
}: {
  foundationPath: Pick<FoundationPath, "condition" | "datums">;
  floorPath: Pick<
    FloorPath,
    "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly" | "leftRoomClass" | "rightRoomClass"
  >;
  foundationAssembly: FoundationAssembly;
  foundationSettings: FoundationSystemSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  level: Level;
}): WorldInches {
  const { curb, stemWall } = buildFoundationAssemblyComponents(foundationComponents, foundationAssembly);

  if (curb) {
    return worldInchesNew(level.floorElevation);
  }

  const { floorPriority, leftRoomClass, rightRoomClass } = floorPath;
  const floorRoomClass = floorPriority === PathPriority.LEFT ? leftRoomClass : rightRoomClass;

  let floorDatum = floorPathTop(level, floorPath);
  if (level.floorIndex > 0 || slabTypeAtRoomClass(floorRoomClass, foundationSettings) === SlabType.NONE) {
    floorDatum = floorPathBottom(level, floorPath, framingComponents, slabComponents, sheathingComponents);
  }

  if (stemWall) {
    const stemTop = stemWallTop({
      foundationPath: { ...foundationPath, assembly: foundationAssembly },
      level,
      stemWall,
      slabSettings: foundationSettings,
    });

    floorDatum = worldInchesNew(Math.max(stemTop, floorDatum));
  }

  return worldInchesNew(floorDatum);
}

export function makeFoundationPathAssemblyOverride(
  floorPath: Pick<FloorPath, "floorPriority">,
  leftAssemblyOverride: FoundationSystemAssemblyOverride<any, any> | undefined,
  rightAssemblyOverride: FoundationSystemAssemblyOverride<any, any> | undefined,
  foundationAssemblies: Table<FoundationAssemblyEntity>,
  foundationComponents: Table<FoundationComponentEntity>,
  foundationSettings: FoundationSystemSettings,
  datumAbstractions: Table<DatumAbstraction>,
) {
  const { floorPriority } = floorPath;

  if (!leftAssemblyOverride && !rightAssemblyOverride) {
    return undefined;
  }

  if (!leftAssemblyOverride) {
    return rightAssemblyOverride;
  }

  if (!rightAssemblyOverride) {
    return leftAssemblyOverride;
  }

  if (floorPriority !== PathPriority.NONE) {
    return floorPriority === PathPriority.LEFT ? leftAssemblyOverride : rightAssemblyOverride;
  }

  const leftAssemblyComponents = leftAssemblyOverride.assemblyId
    ? foundationAssemblies[leftAssemblyOverride.assemblyId]
    : undefined;
  const rightAssemblyComponents = rightAssemblyOverride.assemblyId
    ? foundationAssemblies[rightAssemblyOverride.assemblyId]
    : undefined;

  if (!leftAssemblyComponents && !rightAssemblyComponents) {
    return undefined;
  }

  if (!leftAssemblyComponents) {
    return rightAssemblyOverride;
  }

  if (!rightAssemblyComponents) {
    return leftAssemblyOverride;
  }

  assertIsNotUndefined(leftAssemblyOverride.assemblyId);
  assertIsNotUndefined(rightAssemblyOverride.assemblyId);

  const leftAssembly = buildFoundationAssemblyComponents(
    foundationComponents,
    makeSolidFoundationAssembly({
      assemblyId: leftAssemblyOverride.assemblyId,
      foundationAssemblyComponents: leftAssemblyComponents,
      foundationComponents,
      foundationSettings,
      datumAbstractions,
    }),
  );

  const rightAssembly = buildFoundationAssemblyComponents(
    foundationComponents,
    makeSolidFoundationAssembly({
      assemblyId: rightAssemblyOverride.assemblyId,
      foundationAssemblyComponents: rightAssemblyComponents,
      foundationComponents,
      foundationSettings,
      datumAbstractions,
    }),
  );

  if (leftAssemblyOverride.assemblySystem === FoundationSubSystemType.MONOLITHIC) {
    let leftFootingWidth: number = 0;
    let rightFootingWidth: number = 0;

    //currently monolithic systems only supports strip and turndown footings. rectangular pad and circular pad are correctly
    //handled below but are not definable in the UI
    //also, such systems may need a beam spanning between the piers or drop panels for them to be practically applied.
    if (leftAssembly.footing) {
      const footingType = leftAssembly.footing.footingType;
      switch (footingType) {
        case FootingType.STRIP:
        case FootingType.TURNDOWN:
        case FootingType.RECTANGULAR_ISOLATED:
          leftFootingWidth = leftAssembly.footing.footingWidth;
          break;
        case FootingType.CIRCULAR_ISOLATED:
          leftFootingWidth = leftAssembly.footing.footingRadius * 2;
          break;
        default:
          assertUnreachable(footingType);
      }
    }
    if (rightAssembly.footing) {
      const footingType = rightAssembly.footing.footingType;
      switch (footingType) {
        case FootingType.STRIP:
        case FootingType.TURNDOWN:
        case FootingType.RECTANGULAR_ISOLATED:
          rightFootingWidth = rightAssembly.footing.footingWidth;
          break;
        case FootingType.CIRCULAR_ISOLATED:
          rightFootingWidth = rightAssembly.footing.footingRadius * 2;
          break;
        default:
          assertUnreachable(footingType);
      }
    }
    return leftFootingWidth > rightFootingWidth ? leftAssemblyOverride : rightAssemblyOverride;
  }

  const leftStemWallWidth = leftAssembly.stemWall ? stemWallBaseWidth({ stemWall: leftAssembly.stemWall }) : 0;
  const rightStemWallWidth = rightAssembly.stemWall ? stemWallBaseWidth({ stemWall: rightAssembly.stemWall }) : 0;

  return leftStemWallWidth > rightStemWallWidth ? leftAssemblyOverride : rightAssemblyOverride;
}

export function buildFoundationAssemblyComponents(
  foundationComponents: Table<FoundationComponentEntity>,
  foundationAssembly: FoundationAssembly,
): {
  stemWall?: StemWallEntity;
  footing?: FootingEntity;
  curb?: CurbEntity;
  pier?: PierEntity;
} {
  if (foundationAssembly.assemblyType === FoundationAssemblyType.VOID) {
    return {};
  }

  const {
    stemWall: stemWallAssemblyComponent,
    footing: footingAssemblyComponent,
    curb: curbAssemblyComponent,
    pier: pierAssemblyComponent,
  } = foundationAssembly;

  let stemWall: StemWallEntity | undefined;
  let footing: FootingEntity | undefined;
  let curb: CurbEntity | undefined;
  let pier: PierEntity | undefined;

  if (stemWallAssemblyComponent) {
    const component =
      stemWallAssemblyComponent.componentId !== undefined
        ? foundationComponents[stemWallAssemblyComponent.componentId]
        : undefined;
    if (component?.componentType === stemWallAssemblyComponent.componentType) {
      stemWall = component;
    }
  }

  if (footingAssemblyComponent) {
    const component =
      footingAssemblyComponent.componentId !== undefined
        ? foundationComponents[footingAssemblyComponent.componentId]
        : undefined;
    if (component?.componentType === footingAssemblyComponent.componentType) {
      footing = component;
    }
  }

  if (curbAssemblyComponent) {
    const component =
      curbAssemblyComponent.componentId !== undefined
        ? foundationComponents[curbAssemblyComponent.componentId]
        : undefined;
    if (component?.componentType === curbAssemblyComponent.componentType) {
      curb = component;
    }
  }

  if (pierAssemblyComponent) {
    const component =
      pierAssemblyComponent.componentId !== undefined
        ? foundationComponents[pierAssemblyComponent.componentId]
        : undefined;
    if (component?.componentType === pierAssemblyComponent.componentType) {
      pier = component;
    }
  }

  return {
    footing,
    stemWall,
    curb,
    pier,
  };
}

export function getFoundationAssemblyBrickLedgeType(
  foundationComponents: Table<FoundationComponentEntity>,
  foundationAssembly: FoundationAssembly,
): {
  brickLedgeType: BrickLedgeType;
  brickLedgeBehavior: BrickLedgeBehavior;
} {
  const { footing, stemWall } = buildFoundationAssemblyComponents(foundationComponents, foundationAssembly);

  if (footing?.footingType === FootingType.TURNDOWN) {
    return footing;
  }

  if (stemWall) {
    return stemWall;
  }

  return {
    brickLedgeType: BrickLedgeType.ADD,
    brickLedgeBehavior: BrickLedgeBehavior.AUTO,
  };
}

export function getFoundationSystemBrickLedgeType(
  foundationAssemblies: Table<FoundationAssemblyEntity>,
  foundationComponents: Table<FoundationComponentEntity>,
  foundationSettings: FoundationSystemSettings,
  datumAbstractions: Table<DatumAbstraction>,
) {
  const foundationAssembly = makeFoundationSubSystemAssembly({
    foundationSettings,
    foundationAssemblies,
    foundationComponents,
    datumAbstractions,
  });
  return getFoundationAssemblyBrickLedgeType(foundationComponents, foundationAssembly);
}

// Foundation Condition Data
export const defaultFoundationConditionDetailNumber: {
  [K in FoundationCondition]: number;
} = {
  [FoundationCondition.HOUSE_EXTERIOR]: 1,
  [FoundationCondition.HOUSE_BRICKLEDGE_EXTERIOR]: 2,
  [FoundationCondition.HOUSE_HOUSE]: 3,
  [FoundationCondition.GARAGE_GARAGE]: 3,
  [FoundationCondition.PORCH_PORCH]: 3,
  [FoundationCondition.PORCH_EXTERIOR]: 4,
  [FoundationCondition.PORCH_BRICKLEDGE_EXTERIOR]: 5,
  [FoundationCondition.HOUSE_PORCH]: 6,
  [FoundationCondition.HOUSE_BRICKLEDGE_PORCH]: 7,
  [FoundationCondition.GARAGE_BRICKLEDGE_EXTERIOR]: 8,
  [FoundationCondition.HOUSE_GARAGE]: 9,
  [FoundationCondition.GARAGE_EXTERIOR]: 10,
  [FoundationCondition.GARAGE_PORCH]: 11,
  [FoundationCondition.GARAGE_BRICKLEDGE_PORCH]: 12,
  [FoundationCondition.GARAGE_DOOR_EXTERIOR]: 13,
  [FoundationCondition.BASEMENT_EXTERIOR]: 14,
  [FoundationCondition.BASEMENT_BRICKLEDGE_EXTERIOR]: 15,
  [FoundationCondition.BASEMENT_BASEMENT]: 16,
  [FoundationCondition.HOUSE_DECK]: 17,
  [FoundationCondition.HOUSE_BRICKLEDGE_DECK]: 18,
  [FoundationCondition.GARAGE_DECK]: 19,
  [FoundationCondition.GARAGE_BRICKLEDGE_DECK]: 20,
  [FoundationCondition.PORCH_DECK]: 21,
  [FoundationCondition.PORCH_BRICKLEDGE_DECK]: 22,
  [FoundationCondition.DECK_DECK]: 23,
  [FoundationCondition.DECK_EXTERIOR]: 24,
  [FoundationCondition.DECK_DISABLED]: 25,
  [FoundationCondition.BASEMENT_DISABLED]: 26,
  [FoundationCondition.GARAGE_DISABLED]: 27,
  [FoundationCondition.PORCH_DISABLED]: 28,
  [FoundationCondition.HOUSE_DISABLED]: 29,
  [FoundationCondition.EXTERIOR_EXTERIOR]: -1,
};
function foundationConditionDataNew(
  assemblyId: string,
  drawingNumber: number,
  drawingId: DrawingId,
): FoundationConditionData {
  return {
    assemblyId,
    drawingNumber,
    drawingId,
  };
}

export function foundationSubSystemTypeForPathAssembly(
  path: Pick<FoundationPath, "assembly">,
  foundationAssemblies: Table<FoundationAssemblyEntity>,
  fallback: FoundationSubSystemType,
): FoundationSubSystemType {
  const { assembly } = path;
  if (!isSolidFoundationAssembly(assembly)) {
    return fallback;
  }
  const assemblyEntity = foundationAssemblies[assembly.assemblyId];
  return assemblyEntity?.assemblySystem ?? fallback;
}

export function defaultFoundationConditionDataNew(
  _: FoundationSuperSystemType,
  foundationSubSystemType: FoundationSubSystemType,
  foundationCondition: FoundationCondition,
  foundationAssemblyId: string = defaultFoundationAssemblyId(foundationSubSystemType),
) {
  const assemblyId = foundationAssemblyId;
  const detailNumber = defaultFoundationConditionDetailNumber[foundationCondition];
  const detailId = DrawingId.NULL;
  return foundationConditionDataNew(assemblyId, detailNumber, detailId);
}

export function foundationSystemAssemblyOverrideNew<S extends FoundationSuperType, T extends FoundationSubType<S>>(
  id: string,
  name: string,
  superSystem: S,
  assemblySystem: T,
): FoundationSystemAssemblyOverride<S, T> {
  return {
    id,
    name,
    superSystem,
    assemblySystem,
    drawingId: DrawingId.NONE,
  };
}

export function getActiveFoundationAssemblyOverride(
  evaluatedFoundationAssemblyOverrides: HalfEdgeAssociationTable<FoundationAssemblyOverrides>,
  halfEdgeIndex: number,
  foundationHashStr: string,
) {
  const foundationOverrides = evaluatedFoundationAssemblyOverrides[halfEdgeIndex]?.[0]?.item;
  if (!foundationOverrides?.length) {
    return undefined;
  }

  // TODO: this could be replaced by the `Array.findLast` method when it is available.
  for (let i = foundationOverrides.length - 1; i >= 0; --i) {
    const foundationOverride = foundationOverrides[i];
    if (
      !foundationOverride.disabled &&
      foundationHashStr === foundationHash(foundationOverride.superSystem, foundationOverride.assemblySystem)
    ) {
      return foundationOverride;
    }
  }
  return undefined;
}
